import { Trash2, X } from 'lucide-react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type { Transaction } from '../../lib/api-types';
import { useDeleteTransaction, useUpsertOverride } from '../transactions/transactions.api';
import { formatLongDate } from './dashboard-format';

export function DeleteMovementDialog({
  occurrenceDate,
  onClose,
  transaction,
}: {
  readonly occurrenceDate?: string;
  readonly onClose: () => void;
  readonly transaction: Transaction;
}) {
  const { locale, t } = useI18n();
  const deleteSeries = useDeleteTransaction();
  const omitOccurrence = useUpsertOverride();
  const dialogRef = useDialogFocusTrap(onClose);
  const isOccurrence = occurrenceDate !== undefined;
  const isPending = deleteSeries.isPending || omitOccurrence.isPending;
  const error = deleteSeries.error ?? omitOccurrence.error;

  const remove = async () => {
    if (occurrenceDate === undefined) {
      await deleteSeries.mutateAsync(transaction.id);
    } else {
      const override = transaction.overrides.find((item) => item.date === occurrenceDate);
      await omitOccurrence.mutateAsync({
        date: occurrenceDate,
        id: transaction.id,
        input: {
          ...(override?.additionalNote === undefined
            ? {}
            : { additionalNote: override.additionalNote }),
          ...(override?.amount === undefined ? {} : { amount: override.amount }),
          disabled: true,
          ...(override?.walletId === undefined ? {} : { walletId: override.walletId }),
        },
      });
    }
    onClose();
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby="delete-movement-title"
        aria-modal="true"
        className="movement-dialog confirmation-sheet"
        ref={dialogRef}
        role="dialog"
      >
        <div className="dialog-heading">
          <div>
            <p className="section-kicker">{t('dashboard.myPlan')}</p>
            <h2 id="delete-movement-title">
              {t(isOccurrence ? 'plan.deleteOccurrence' : 'plan.deleteSeries')}
            </h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <p className="movement-delete-warning" role="alert">
          {isOccurrence
            ? t('plan.omitOccurrenceWarning', {
                date: formatLongDate(occurrenceDate, locale),
              })
            : t('dashboard.deleteMovementWarning')}
        </p>
        {error !== null && (
          <p className="form-error" role="alert">
            {t('dashboard.saveError')}
          </p>
        )}
        <div className="dialog-actions">
          <button className="button button-ghost" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="button button-danger"
            disabled={isPending}
            type="button"
            onClick={() => void remove()}
          >
            <Trash2 aria-hidden="true" />
            {t(isOccurrence ? 'plan.confirmOmitOccurrence' : 'dashboard.confirmDeleteMovement')}
          </button>
        </div>
      </section>
    </div>
  );
}
